import { useMemo } from 'react';
import { GLYPHS } from '@frameio/utils/constants';
import {
  Text,
  Tooltip,
  TooltipDetails,
  TooltipTitle,
  VisuallyHidden,
} from '@frameio/vapor';
import { getScreenReaderTextForTimestamp } from '../../../utils';
import type { NotificationGroupInfo } from '../types';
import styles from './Footer.module.css';
import TEXT from './generated/TEXT/NotificationCellFooter';
import { useFooterData } from './useFooterData';

TEXT.DEFINE({
  /** Prefix for resource tooltip based on resource type */
  tooltipPrefix: (resourceType: string | undefined) => {
    if (resourceType === 'NotificationLayoutItemProjectName')
      return 'Project: ';
    if (resourceType === 'NotificationLayoutItemShareName') return 'Share: ';
    return '';
  },
});

export function Footer({
  id,
  notificationType,
  actions,
}: NotificationGroupInfo & { actions?: React.ReactNode }) {
  const data = useFooterData({
    id,
    notificationType,
  });

  const getTooltipPrefix = TEXT.tooltipPrefix;

  const entityTooltip = useMemo(() => {
    if (!data) return null;
    const { resourceName, resourceType } = data;
    const tooltipPrefix = getTooltipPrefix(resourceType);

    return (
      resourceName && (
        <>
          <Tooltip
            placement="top"
            title={
              <TooltipTitle>
                <TooltipDetails style={{ display: 'inline' }}>
                  {tooltipPrefix}
                </TooltipDetails>
                {resourceName}
              </TooltipTitle>
            }
          >
            {/** wrapping <div /> to anchor Tooltip */}
            <div
              className={styles.entityContainer}
              data-testid="FooterEntityName"
            >
              <Text
                aria-hidden={true}
                as="p"
                color="tertiary"
                display="inline"
                truncate={true}
                variant="secondary"
              >
                {resourceName}
              </Text>
              <VisuallyHidden>
                {tooltipPrefix}
                {resourceName}
              </VisuallyHidden>
            </div>
          </Tooltip>
          <div className={styles.timestamp}>
            <Text aria-hidden={true} color="tertiary" variant="secondary">
              {GLYPHS.bullet}
            </Text>
          </div>
        </>
      )
    );
  }, [data, getTooltipPrefix]);

  const timestampTooltip = useMemo(() => {
    if (!data) return null;
    const { timestamp, formattedTimestamp, formattedDate } = data;
    return (
      <Tooltip
        placement="top"
        title={<TooltipTitle>{formattedDate}</TooltipTitle>}
      >
        {/** wrapping <div /> to anchor Tooltip */}
        <div>
          <Text
            aria-hidden={true}
            as="p"
            color="tertiary"
            data-testid="notification-footer-timestamp"
            truncate={4}
            variant="secondary"
          >
            {formattedTimestamp}
          </Text>
          <VisuallyHidden>
            {getScreenReaderTextForTimestamp({ timestamp })}
          </VisuallyHidden>
        </div>
      </Tooltip>
    );
  }, [data]);

  return (
    <>
      <div
        className={styles.timestampContainer}
        data-testid="notification-footer"
      >
        {entityTooltip}

        <div className={styles.timestamp}>{timestampTooltip}</div>
      </div>
      {actions}
    </>
  );
}
